import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    FiGrid, FiFileText, FiLayers, FiCheckCircle,
    FiLogOut, FiSettings, FiBriefcase, FiTrash2, FiEdit2, FiPlus, FiUser,
    FiDownload, FiMessageSquare, FiMail, FiCalendar, FiClock, FiCheck
} from 'react-icons/fi';
import api, { getAssetUrl } from '../../utils/api';

const DashboardContainer = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: 280px 1fr;
  background: #0a0a0c;
  color: white;
`;

const Sidebar = styled.aside`
  background: rgba(255, 255, 255, 0.02);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  height: 100vh;
  position: sticky;
  top: 0;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #7c3aed 0%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 2rem;
`;

const NavList = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NavItem = styled.button`
  background: ${props => props.active ? 'rgba(124, 58, 237, 0.1)' : 'transparent'};
  border: 1px solid ${props => props.active ? 'rgba(124, 58, 237, 0.2)' : 'transparent'};
  color: ${props => props.active ? '#7c3aed' : 'rgba(255, 255, 255, 0.5)'};
  padding: 0.8rem 1rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }
`;

const MainContent = styled.main`
  padding: 3rem;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 1.2rem;
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: ${props => props.color || '#7c3aed'}20;
  color: ${props => props.color || '#7c3aed'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [user, setUser] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await api.get('/auth');
                setUser(res.data);
            } catch (err) {
                localStorage.removeItem('token');
                navigate('/admin/login');
            }
        };
        fetchUser();
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/admin/login');
    };

    if (!user) return null;

    return (
        <DashboardContainer>
            <Sidebar>
                <Logo>Portfolio Admin</Logo>
                <NavList>
                    <NavItem active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                        <FiGrid /> Overview
                    </NavItem>
                    <NavItem active={activeTab === 'projects'} onClick={() => setActiveTab('projects')}>
                        <FiLayers /> Projects
                    </NavItem>
                    <NavItem active={activeTab === 'timeline'} onClick={() => setActiveTab('timeline')}>
                        <FiBriefcase /> Timeline
                    </NavItem>
                    <NavItem active={activeTab === 'blogs'} onClick={() => setActiveTab('blogs')}>
                        <FiFileText /> Blogs
                    </NavItem>
                    <NavItem active={activeTab === 'resume'} onClick={() => setActiveTab('resume')}>
                        <FiDownload /> Resume
                    </NavItem>
                    <NavItem active={activeTab === 'messages'} onClick={() => setActiveTab('messages')}>
                        <FiMessageSquare /> Messages
                    </NavItem>
                    <NavItem active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
                        <FiSettings /> Daily Stats
                    </NavItem>
                </NavList>
                <NavItem onClick={handleLogout} style={{ marginTop: 'auto', color: '#ef4444' }}>
                    <FiLogOut /> Logout
                </NavItem>
            </Sidebar>

            <MainContent>
                <Header>
                    <div>
                        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Welcome back, {user.name}</h1>
                        <p style={{ color: 'rgba(255,255,255,0.5)' }}>Manage your portfolio content and statistics.</p>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => window.open('/', '_blank')}
                        style={{
                            padding: '0.8rem 1.5rem',
                            borderRadius: '12px',
                            background: '#7c3aed',
                            border: 'none',
                            color: 'white',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        Visit Site
                    </motion.button>
                </Header>

                {activeTab === 'overview' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <StatsGrid>
                            <StatCard>
                                <StatIcon color="#7c3aed"><FiLayers /></StatIcon>
                                <div>
                                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>Total Projects</p>
                                    <h3 style={{ fontSize: '1.5rem' }}>Dynamic</h3>
                                </div>
                            </StatCard>
                            <StatCard>
                                <StatIcon color="#2563eb"><FiCheckCircle /></StatIcon>
                                <div>
                                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>Messages</p>
                                    <h3 style={{ fontSize: '1.5rem' }}>Check Contact</h3>
                                </div>
                            </StatCard>
                            <StatCard>
                                <StatIcon color="#10b981"><FiUser /></StatIcon>
                                <div>
                                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>Auth Type</p>
                                    <h3 style={{ fontSize: '1.5rem' }}>Admin</h3>
                                </div>
                            </StatCard>
                        </StatsGrid>

                        <div style={{
                            background: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            padding: '4rem',
                            borderRadius: '24px',
                            textAlign: 'center',
                            color: 'rgba(255,255,255,0.5)'
                        }}>
                            <FiBriefcase size={40} style={{ marginBottom: '1rem' }} />
                            <h3>Select a tab on the left to manage your content</h3>
                        </div>
                    </motion.div>
                )}

                {activeTab === 'settings' && (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                    >
                        <h2 style={{ marginBottom: '2rem' }}>Manage Daily Stats</h2>
                        <StatsManagement />
                    </motion.div>
                )}

                {activeTab === 'resume' && (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                    >
                        <h2 style={{ marginBottom: '2rem' }}>Manage Resume</h2>
                        <ResumeManagement />
                    </motion.div>
                )}

                {activeTab === 'messages' && (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                    >
                        <h2 style={{ marginBottom: '2rem' }}>Contact Messages</h2>
                        <MessagesManagement />
                    </motion.div>
                )}

                {(activeTab === 'timeline' || activeTab === 'blogs') && (
                    <div style={{ textAlign: 'center', padding: '5rem', color: 'rgba(255,255,255,0.3)' }}>
                        <h3>Tab "{activeTab}" is coming soon!</h3>
                        <p>Currently focusing on Daily Stats and Projects as requested.</p>
                    </div>
                )}
            </MainContent>
        </DashboardContainer>
    );
};

const StatsManagement = () => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await api.get('/config');
                setStats(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    const handleUpdate = async (key, value, label) => {
        setSaving(true);
        try {
            await api.post('/config', { key, value, label });
            // Refresh stats
            const res = await api.get('/config');
            setStats(res.data);
        } catch (err) {
            alert('Error updating stat');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div>Loading Stats...</div>;

    return (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
            {stats.map(stat => (
                <div key={stat.key} style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    padding: '1.5rem',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginBottom: '0.2rem' }}>{stat.label}</p>
                        <input
                            type="text"
                            defaultValue={stat.value}
                            onBlur={(e) => handleUpdate(stat.key, e.target.value, stat.label)}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'white',
                                fontSize: '1.2rem',
                                fontWeight: '700',
                                width: '100px',
                                outline: 'none'
                            }}
                        />
                    </div>
                    <FiEdit2 style={{ color: 'rgba(255,255,255,0.3)' }} />
                </div>
            ))}
            {saving && <p style={{ color: '#7c3aed', fontSize: '0.8rem' }}>Saving changes...</p>}
        </div>
    );
};

const ProjectsManagement = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const res = await api.get('/projects');
                setProjects(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchProjects();
    }, []);

    const deleteProject = async (id) => {
        if (!window.confirm('Are you sure you want to delete this project?')) return;
        try {
            await api.delete(`/projects/${id}`);
            setProjects(projects.filter(p => p._id !== id));
        } catch (err) {
            alert('Error deleting project');
        }
    };

    if (loading) return <div>Loading Projects...</div>;

    return (
        <div style={{ display: 'grid', gap: '1rem' }}>
            {projects.map(project => (
                <div key={project._id} style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    padding: '1.2rem',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                        <img
                            src={getAssetUrl(project.image)}
                            alt=""
                            style={{ width: '60px', height: '60px', borderRadius: '12px', objectFit: 'cover' }}
                        />
                        <div>
                            <h4 style={{ marginBottom: '0.2rem' }}>{project.title}</h4>
                            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>{project.category}</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                            <FiEdit2 size={18} />
                        </button>
                        <button
                            onClick={() => deleteProject(project._id)}
                            style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                        >
                            <FiTrash2 size={18} />
                        </button>
                    </div>
                </div>
            ))}
            {projects.length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.2)' }}>
                    No projects found. Add your first project!
                </div>
            )}
        </div>
    );
};

const ResumeManagement = () => {
    const [uploading, setUploading] = useState(false);
    const [resumeUrl, setResumeUrl] = useState('');

    useEffect(() => {
        const fetchResume = async () => {
            try {
                const res = await api.get('/config');
                const resume = res.data.find(c => c.key === 'resumeUrl');
                if (resume) setResumeUrl(resume.value);
            } catch (err) {
                console.error(err);
            }
        };
        fetchResume();
    }, []);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('resume', file);

        setUploading(true);
        try {
            const res = await api.post('/uploads/resume', formData);
            const filePath = res.data.data.filePath;
            await api.post('/config', { key: 'resumeUrl', value: filePath, label: 'Resume URL' });
            setResumeUrl(filePath);
            alert('Resume uploaded successfully!');
        } catch (err) {
            console.error(err);
            alert('Error uploading resume');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '2rem',
            borderRadius: '24px'
        }}>
            <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ marginBottom: '1rem' }}>Current Resume</h3>
                {resumeUrl ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <FiFileText size={40} color="#7c3aed" />
                        <div>
                            <p style={{ fontWeight: '600' }}>resume.pdf</p>
                            <a href={getAssetUrl(resumeUrl)} target="_blank" rel="noreferrer" style={{ color: '#7c3aed', fontSize: '0.9rem' }}>
                                View Current Resume
                            </a>
                        </div>
                    </div>
                ) : (
                    <p style={{ color: 'rgba(255,255,255,0.4)' }}>No resume uploaded yet.</p>
                )}
            </div>

            <div>
                <h3 style={{ marginBottom: '1rem' }}>Upload New Resume (PDF Only)</h3>
                <div style={{
                    border: '2px dashed rgba(255,255,255,0.1)',
                    padding: '2rem',
                    borderRadius: '16px',
                    textAlign: 'center',
                    position: 'relative'
                }}>
                    {uploading ? (
                        <p>Uploading...</p>
                    ) : (
                        <>
                            <FiDownload size={30} style={{ marginBottom: '1rem', color: 'rgba(255,255,255,0.3)' }} />
                            <p style={{ marginBottom: '1rem' }}>Drag and drop or click to select</p>
                            <input
                                type="file"
                                accept=".pdf"
                                onChange={handleUpload}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    opacity: 0,
                                    cursor: 'pointer'
                                }}
                            />
                            <button style={{
                                padding: '0.5rem 1.5rem',
                                borderRadius: '8px',
                                background: 'rgba(255,255,255,0.1)',
                                border: 'none',
                                color: 'white',
                                pointerEvents: 'none'
                            }}>
                                Choose PDF
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const MessagesManagement = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMessages = async () => {
            try {
                const res = await api.get('/contact');
                setMessages(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchMessages();
    }, []);

    const markAsRead = async (id) => {
        try {
            await api.put(`/contact/${id}`);
            setMessages(messages.map(m => m._id === id ? { ...m, isRead: true } : m));
        } catch (err) {
            console.error(err);
        }
    };

    const deleteMessage = async (id) => {
        if (!window.confirm('Are you sure you want to delete this message?')) return;
        try {
            await api.delete(`/contact/${id}`);
            setMessages(messages.filter(m => m._id !== id));
        } catch (err) {
            console.error(err);
            alert('Error deleting message');
        }
    };

    if (loading) return <div>Loading Messages...</div>;

    return (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
            {messages.map(msg => (
                <div key={msg._id} style={{
                    background: msg.isRead ? 'rgba(255,255,255,0.02)' : 'rgba(124, 58, 237, 0.05)',
                    border: '1px solid' + (msg.isRead ? ' rgba(255,255,255,0.05)' : ' rgba(124, 58, 237, 0.2)'),
                    padding: '1.5rem',
                    borderRadius: '20px',
                    position: 'relative'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: '#7c3aed20',
                                color: '#7c3aed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <FiUser />
                            </div>
                            <div>
                                <h4 style={{ marginBottom: '0.2rem' }}>{msg.name}</h4>
                                <div style={{ display: 'flex', gap: '1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                                    <span><FiMail style={{ verticalAlign: 'middle', marginRight: '4px' }} /> {msg.email}</span>
                                    <span><FiCalendar style={{ verticalAlign: 'middle', marginRight: '4px' }} /> {new Date(msg.createdAt).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {!msg.isRead && (
                                <button
                                    onClick={() => markAsRead(msg._id)}
                                    title="Mark as read"
                                    style={{ background: 'transparent', border: 'none', color: '#10b981', cursor: 'pointer' }}
                                >
                                    <FiCheck size={18} />
                                </button>
                            )}
                            <button
                                onClick={() => deleteMessage(msg._id)}
                                title="Delete message"
                                style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                            >
                                <FiTrash2 size={18} />
                            </button>
                        </div>
                    </div>
                    <div style={{ paddingLeft: '3.5rem' }}>
                        <p style={{ color: 'rgba(255,255,255,0.8)', lineHeight: '1.6' }}>{msg.message}</p>
                    </div>
                </div>
            ))}
            {messages.length === 0 && (
                <div style={{ textAlign: 'center', padding: '5rem', color: 'rgba(255,255,255,0.2)' }}>
                    <FiMail size={40} style={{ marginBottom: '1rem' }} />
                    <h3>No messages yet</h3>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
